import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, extends as _extends, objectSpread2 as _objectSpread2 } from '../../_virtual/_rollupPluginBabelHelpers.js';
import propTypes from '../../node_modules/prop-types/index.js';
import React, { useMemo, useCallback } from 'react';
import { useFormControl } from '@mui/material';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import usePickerInputValue from './usePickerInputValue.js';
import usePickerOpenState from './usePickerOpenState.js';
import { AdapterLuxon } from '../../node_modules/@mui/x-date-pickers/AdapterLuxon/AdapterLuxon.js';

var _excluded = ["value", "onChange", "size", "required", "error", "disabled", "slotProps", "minTime", "maxTime"];
var timeTests = [{
  format: "HH:mm",
  regex: /^\d{2}:\d{2}$/
}, {
  format: "HH:mm:ss",
  regex: /^\d{2}:\d{2}:\d{2}$/
}];
var timeFormat = "HH:mm:ss";

var stringToLocalTime = function stringToLocalTime(v) {
  if (!v) return null;
  var timeTest = timeTests.find(function (t) {
    return t.regex.test(v);
  });

  if (!timeTest) {
    var validFormats = timeTests.map(function (t) {
      return t.format;
    });
    console.error("TimeLocal only accepts string value '".concat(validFormats.join("' or '"), "'"));
    return null;
  }

  return DateTime.fromFormat(v, timeTest.format);
};

var localTimeToString = function localTimeToString(v) {
  return v.toFormat(timeFormat);
};

var TIME_LOCAL_FORMAT = timeFormat;

/**
 * Time field for local times (to the user)
 */
var TimeLocal = function TimeLocal(_ref) {
  var _bounds$min, _bounds$max;

  var value = _ref.value,
      onChange = _ref.onChange,
      size = _ref.size,
      required = _ref.required,
      error = _ref.error,
      disabled = _ref.disabled,
      slotProps = _ref.slotProps,
      minTime = _ref.minTime,
      maxTime = _ref.maxTime,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  var formControl = useFormControl();
  var pickerValue = useMemo(function () {
    if (!value) return null;
    return stringToLocalTime(value);
  }, [value]);

  var _usePickerInputValue = usePickerInputValue(pickerValue),
      _usePickerInputValue2 = _slicedToArray(_usePickerInputValue, 2),
      inputValue = _usePickerInputValue2[0],
      setInputValue = _usePickerInputValue2[1];

  var _usePickerOpenState = usePickerOpenState(),
      open = _usePickerOpenState.open,
      onOpen = _usePickerOpenState.onOpen,
      onClose = _usePickerOpenState.onClose,
      popperProps = _usePickerOpenState.popperProps;

  var pickerChange = useCallback(function (v, _ref2) {
    var validationError = _ref2.validationError;
    setInputValue(v);
    if (!onChange) return;

    if (!v || !v.isValid || !!validationError) {
      onChange(null);
      return;
    }

    onChange(localTimeToString(v));
  }, [onChange]);
  var bounds = useMemo(function () {
    return {
      min: typeof minTime === "string" ? stringToLocalTime(minTime) : minTime,
      max: typeof maxTime === "string" ? stringToLocalTime(maxTime) : maxTime
    };
  }, [minTime, maxTime]);
  var textFieldProps = slotProps === null || slotProps === void 0 ? void 0 : slotProps.textField;
  return /*#__PURE__*/React.createElement(LocalizationProvider, {
    dateAdapter: AdapterLuxon
  }, /*#__PURE__*/React.createElement(TimePicker, _extends({}, otherProps, {
    value: inputValue,
    onChange: pickerChange,
    open: open,
    onOpen: onOpen,
    onClose: onClose,
    disabled: disabled !== null && disabled !== void 0 ? disabled : formControl === null || formControl === void 0 ? void 0 : formControl.disabled,
    slotProps: _objectSpread2(_objectSpread2({}, slotProps), {}, {
      popper: _objectSpread2(_objectSpread2({}, slotProps === null || slotProps === void 0 ? void 0 : slotProps.popper), popperProps),
      textField: _objectSpread2(_objectSpread2({}, textFieldProps), {}, {
        required: required !== null && required !== void 0 ? required : formControl === null || formControl === void 0 ? void 0 : formControl.required,
        error: error || formControl !== null && formControl !== void 0 && formControl.error ? true : undefined,
        // undefined allows internal validation errors to show
        size: size,
        onBlur: function onBlur(e) {
          if (inputValue && !pickerValue) setInputValue(null);
          if (textFieldProps !== null && textFieldProps !== void 0 && textFieldProps.onBlur) textFieldProps.onBlur(e);
        }
      })
    }),
    minTime: (_bounds$min = bounds.min) !== null && _bounds$min !== void 0 ? _bounds$min : undefined,
    maxTime: (_bounds$max = bounds.max) !== null && _bounds$max !== void 0 ? _bounds$max : undefined
  })));
};

TimeLocal.propTypes = {
  value: propTypes.oneOfType([propTypes.string, propTypes.oneOf([null])]),
  onChange: propTypes.func,
  required: propTypes.bool,
  error: propTypes.bool,
  minTime: propTypes.oneOfType([propTypes.any, propTypes.string, propTypes.oneOf([null])]),
  maxTime: propTypes.oneOfType([propTypes.any, propTypes.string, propTypes.oneOf([null])])
};
TimeLocal.defaultProps = {
  ampm: false,
  format: "HH:mm"
};
var TimeLocal$1 = TimeLocal;

export { TIME_LOCAL_FORMAT, TimeLocal$1 as default };
